<template>
  <div class="result bg-white" v-loading="loading">
    <div :class="['container', { 'py-8': !xs, 'py-4': xs }]">
      <el-row>
        <el-col :span="18" :offset="xs ? 0 : 6" :xs="24">
          <SearchTab :type="type" @tab-change="handleTabChange" />
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="6" :xs="24">
          <div class="bg-white-1 inherit-h">
            <div
              class="bg-primary-sub text-white fs-lg"
              style="padding: 0.75rem; line-height: 20px"
            >
              自定义检索
            </div>
            <FormItem
              ref="formItem"
              class="p-3"
              :fields="formFields"
              :yearOptions="yearOptions"
              @on-search="onSearch"
              @year-change="handleChangeYear"
              @reset="reset"
              :type="type"
            />
          </div>
        </el-col>
        <el-col
          :span="18"
          :xs="24"
          id="table-view"
          style="border: 1px solid #d4d4d4"
        >
          <div class="result-box">
            <!-- 结果-表格 -->
            <div class="result-table">
              <TableItem
                :tableData="result.list"
                :columns="columns"
                size="large"
              />
            </div>
          </div>
        </el-col>
      </el-row>
      <div
        class="result-table-footer pt-4 pb-4 flex-one jc-end"
        v-if="result.total"
      >
        <el-pagination
          @current-change="currentPageChange"
          @size-change="pageSizeChange"
          background
          layout="sizes, prev, pager, next"
          :total="result.total"
          v-model:page-size="result.pageSize"
          v-model:currentPage="currentPage"
          :small="xs ? true : false"
          ref="pagination"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, watch, toRaw, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import {
  hospitalRank,
  universityRank,
  hospitalData,
  universityData,
  universityAreaRanking,
  hospitalAreaRanking
} from '@/api/ranking.js'
import { setPV } from '@/api/config.js'

import { api_types } from '@/config/sysEnum.js'
import { TABLE_COLUMNS } from '@/config/constant.js'

import useSize from '@/hooks/useSize.js'

import TableItem from '@/components/table/index.vue'

import SearchTab from './components/search-tab/index.vue'
import FormItem from './components/form/index.vue'

import INFO from './constant.js'

const route = useRoute()
const { getters } = useStore()

const { xs } = useSize()

const loading = ref(true)

const formItem = ref(null)
const pagination = ref(null)
// 结果类型 医院/医学院校
const type = ref(route.params?.type) //页面类型
const info = INFO[type.value] //页面信息
document.title = `${info.name}STEM结果 - ${document.title.split(' - ')[1]}`

const tabType = ref('1') //tab

const isProvinceSearch = ref(false)

const yearOptions = ref({
  current: '',
  year: []
})

/* table columns */
const columns = computed(() => {
  return TABLE_COLUMNS.map((item) => {
    return {
      ...item,
      align: 'left',
      name:
        item.prop === 'name'
          ? type.value === 'college'
            ? '医学院校'
            : '医院'
          : item.name
    }
  })
})

const currentSearchInfo = reactive({
  name: '',
  year: '',
  code: [],
  province: '全国'
})

//当前可检索字段
const formFields = computed(() => {
  const fields = ['area', 'name', 'year']
  tabType.value == 2 ? fields.push('subject') : null
  return fields
})

const setYear = () => {
  const apiType = api_types.getValueByLabel(`${type.value}_${tabType.value}`)
  yearOptions.value = {
    current: getters.currentYear[`${apiType.toUpperCase()}`],
    year: getters.years[`${apiType.toUpperCase()}`]
  }
  currentSearchInfo.year = yearOptions.value.current
}

watch(
  () => tabType.value,
  () => {
    setYear()
  },
  {
    immediate: true
  }
)

/**
 * 滚动页面 type: 1-tab位置 2-table位置
 */
const scrollTo = (type = 1) => {
  nextTick(() => {
    const offset1 = type === 1 ? 110 : 475
    const offset2 = type === 1 ? 140 : 110
    window.scrollTo({ top: xs.value ? offset1 : offset2, behavior: 'smooth' })
  })
}

const setQueryInfo = ({ name, subject, year, province }) => {
  currentSearchInfo.name = name ? name : ''
  currentSearchInfo.year = year ? year : yearOptions.value.current
  currentSearchInfo.code = subject ? subject : currentSearchInfo.code
  currentSearchInfo.province = province ? province : '全国'
}

//检索
const onSearch = (params) => {
  params.province = params.area
  setQueryInfo(params)
  scrollTo(2)
  isProvinceSearch.value = true
  getResult()
}
//重置
const reset = (params) => {
  params.province = params.area
  setQueryInfo(params)
  scrollTo(2)
  getResult()
}

const currentPage = ref(1)
const result = reactive({
  pageSize: 20,
  total: 0,
  list: []
})

//改变page
const currentPageChange = (value) => {
  currentPage.value = value
  scrollTo(2)
  getResult()
}

const pageSizeChange = () => {
  currentPageChange(1)
}

//获取结果
const getResult = async () => {
  loading.value = true
  const { code, year, name, province } = currentSearchInfo
  const codeValue = code && code.length ? code[1] : ''
  let r
  if (name) {
    const query = {
      hospitalName: name,
      year: [year]
    }
    if (codeValue) query.code = [codeValue]
    r =
      type.value === 'college'
        ? await universityData(query)
        : await hospitalData(query)
    result.list = r.length
      ? [
          {
            ...r[0],
            name
          }
        ]
      : []
    result.total = r.length
    currentPage.value = 1
  } else {
    //省份排名
    if (isProvinceSearch.value && province !== '全国') {
      const query = {
        year: year,
        province
      }
      if (codeValue) query.code = codeValue
      r =
        type.value === 'college'
          ? await universityAreaRanking(query)
          : await hospitalAreaRanking(query)
      result.list = r
      isProvinceSearch.value = false
      result.total = 0
    } else {
      const query = {
        pageNum: currentPage.value,
        pageSize: result.pageSize,
        year: year
      }
      if (codeValue) query.code = codeValue
      r =
        type.value === 'college'
          ? await universityRank(query)
          : await hospitalRank(query)
      result.list = r.records
      result.total = r.total * 1
    }
  }
  loading.value = false
}

//改变年份
const handleChangeYear = (year) => {
  currentSearchInfo.year = year
}

//tab
const handleTabChange = async (value) => {
  scrollTo(1)
  tabType.value = value
  result.total = 0
  currentPage.value = 1
  // currentSearchInfo.code = []
  setQueryInfo({ ...toRaw(formItem.value.formModel), subject: [] })
  let timer = setTimeout(() => {
    formItem.value.reset()
    clearTimeout(timer)
    timer = null
  }, 0)
  // getResult()
  await setPV()
}

//mounted
onMounted(async () => {
  scrollTo(1)
  getResult()
  await setPV()
})
</script>

<style lang="scss" scoped>
.result {
  &-table {
    &-footer {
    }
  }
}
@media screen and (max-width: 500px) {
  .result {
    .container {
      width: 100% !important;
    }
  }
}
</style>

<style lang="scss">
.custom-dialog {
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0.9375rem;
    // width: max-content;
  }
}

@media screen and (min-width: 500px) {
  .result .el-table .cell {
    line-height: 17px !important;
  }
}

@media screen and (max-width: 500px) {
  .result .el-table .cell {
    line-height: 20px !important;
  }
}
</style>
